import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import type { ResourceRequestDetailView } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { hashTrackingId } from './hashTrackingId';

const RESOURCE_REQUEST_EVENT_NAMES: Record<string, string> = {
  CLICK_NAV_ICON: 'click_resource_request_nav_icon',
  CLICK_NEW_REQUEST: 'click_resource_panel_new_request',
  CLICK_QUEUE_ITEM: 'click_resource_request_queue_item',
  CLICK_EXTERNAL_ORDER: 'click_external_resource_request_order',
};

interface TrackNavIconClickParams {
  /** Whether the panel is being opened or closed by this click */
  panelAction: 'open' | 'close';
}

/**
 * Tracks when a user clicks the Resource Request nav icon in the header.
 */
export const trackResourceRequestNavIconClick = ({
  panelAction,
}: TrackNavIconClickParams): void => {
  gspx.trackEvent({
    name: RESOURCE_REQUEST_EVENT_NAMES.CLICK_NAV_ICON,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.RESOURCE_REQUEST,
    capability: GPX_CAPABILITIES.RESOURCE_REQUEST,
    route: GPX_ROUTES.RESOURCE_REQUEST_PANEL,
    additionalKeys: {
      panel_action: panelAction,
    },
  });
};

/**
 * Tracks when a user clicks the "+ New Request" button in the resource request list.
 */
export const trackNewResourceRequestClick = (): void => {
  gspx.trackEvent({
    name: RESOURCE_REQUEST_EVENT_NAMES.CLICK_NEW_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.RESOURCE_REQUEST,
    capability: GPX_CAPABILITIES.RESOURCE_REQUEST,
    route: GPX_ROUTES.RESOURCE_REQUEST_PANEL,
  });
};

interface TrackQueueItemClickParams {
  /** The resource type, e.g. air_ambulance */
  resourceType: ResourceRequestDetailView;
  /** The ID of the request */
  requestId: string;
  /** Display label derived from ingestion_source and requestMethod */
  sourceLabel: string;
}

/**
 * Tracks when a user clicks any row in the resource request queue list.
 */
export const trackResourceRequestQueueItemClick = ({
  resourceType,
  requestId,
  sourceLabel,
}: TrackQueueItemClickParams): void => {
  gspx.trackEvent({
    name: RESOURCE_REQUEST_EVENT_NAMES.CLICK_QUEUE_ITEM,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.RESOURCE_REQUEST,
    capability: GPX_CAPABILITIES.RESOURCE_REQUEST,
    route: GPX_ROUTES.RESOURCE_REQUEST_PANEL,
    entities: {
      request_id: hashTrackingId(requestId),
    },
    additionalKeys: {
      resource_type: resourceType,
      source_label: sourceLabel,
    },
  });
};

interface TrackExternalOrderClickParams {
  /** The resource type, such as air_ambulance */
  resourceType: ResourceRequestDetailView;
  /** The ID of the request */
  requestId: string;
  /** Raw ingestion_source value from the transport */
  ingestionSource: string | null | undefined;
  /** Raw requestMethod from verbatim_message */
  requestMethod: string | null | undefined;
  /** Display label derived from ingestion_source and requestMethod */
  sourceLabel: string;
}

/**
 * Tracks when a user clicks a resource request row that originated outside UNITE
 * aka ingestion_source is not "unite".
 */
export const trackExternalResourceRequestOrderClick = ({
  resourceType,
  requestId,
  ingestionSource,
  requestMethod,
  sourceLabel,
}: TrackExternalOrderClickParams): void => {
  gspx.trackEvent({
    name: RESOURCE_REQUEST_EVENT_NAMES.CLICK_EXTERNAL_ORDER,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.RESOURCE_REQUEST,
    capability: GPX_CAPABILITIES.RESOURCE_REQUEST,
    route: GPX_ROUTES.RESOURCE_REQUEST_PANEL,
    entities: {
      request_id: hashTrackingId(requestId),
    },
    additionalKeys: {
      resource_type: resourceType,
      ingestion_source: ingestionSource ?? null,
      request_method: requestMethod ?? null,
      source_label: sourceLabel,
    },
  });
};
